<template>
  <v-row justify="center">

    <!-- Caller Verification Method Selection Dialog -->
    <v-dialog
      v-model="dialogSelectMethod"
      persistent
      max-width="625"
    >
      <v-card>
        <v-card-title>
          <span v-if="isRegularVerify" v-translate='{caller: callerName}'>Choose a method to verify caller: "%{ caller }"</span>
          <span v-else-if="canVerify" v-translate='{caller: callerName}'>Choose a method to verify manager: "%{ caller }"</span>
          <span v-else v-translate='{failreason: getFailReason}'>
            The manager can't be verified because %{ failreason }
          </span>
          <span v-if="isRegularVerify || canVerify">
                  &nbsp;
                  <v-tooltip v-if="{callerEmail}" bottom>
                    <template v-slot:activator="{on, attrs}">
                      <v-icon v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ callerEmail }}</span>
                  </v-tooltip>
          </span>
        </v-card-title>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" v-translate>Factor description</th>
                <th class="text-left ma-0 pa-0" width="1%"></th>
                <th class="text-right" v-translate>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(factor, factorId) in activePendingDisabledFactors" :key="factorId">
                <td v-translate='{factorDescription: $cvutils.getTranslatedFactorDesc(factor)}'>%{ factorDescription }</td>
                <td class="ma-0 pa-0">
                  <v-tooltip v-if="factor.profile.tooltip" right>
                    <template v-slot:activator="{on, attrs}">
                      <v-icon v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ factor.profile.tooltip }}</span>
                  </v-tooltip>
                </td>
                <td class="text-right">
                  <v-btn v-if="factor.status === 'ACTIVE'"
                    small
                    :id="'verify_'+factor.type+'_'+(factor.provider === 'oauth'? factorId : factor.provider)"
                    color="success"
                    @click="verifyCallerInitiate(factorId)"
                  >
                    <translate>Verify</translate>
                  </v-btn>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <div v-on="on" class="d-inline-block">
                        <v-btn v-if="factor.status === 'DISABLED'"
                          small
                          :id="'verify_'+factor.type+'_'+(factor.provider === 'oauth'? factorId : factor.provider)"
                          color="success"
                          :disabled="true"
                        >
                          <translate>Verify</translate>
                        </v-btn>
                      </div>
                    </template>
                    <translate>Verification method disabled due to caller status</translate>
                  </v-tooltip>
                  <v-tooltip v-if="factor.status === 'PENDING_ACTIVATION'" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-on="on"
                        :id="'verify_pending_'+factor.type+'_'+factor.provider"
                        v-bind="attrs"
                        small
                        label
                        color="info"
                      >
                        <translate>PENDING ACTIVATION</translate>
                      </v-chip>
                    </template>
                    <span><translate>This factor is pending activation</translate></span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            id="verify_choose_cancel"
            @click="$emit('close', 'mfa-verify')"
          >
            <translate>Cancel</translate>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- TOPT, Email, SMS and Security Question Verification Dialog -->
    <v-dialog
      v-model="dialogVerifyResponse"
      persistent
      max-width="625"
    >
      <v-card>
        <v-card-title v-if="selectedFactor !== null" id="verify_title" v-translate='{factorDescription: $cvutils.getTranslatedFactorDesc(selectedFactor) }'>
          Caller Verification: %{ factorDescription }
        </v-card-title>

        <!-- NOTE: responseHint is translated before it is set, elsewhere in the javascript -->
        <div class="ma-5">

          <!-- Label for factors that require a code to be supplied by the caller: TOTP, SMS, Email -->
          <label
            v-if="otpFactors.includes(selectedFactor.type)" for="callerResponse"
            v-translate="{factor: this.$cvutils.getTranslatedFactorDesc(selectedFactor) }">
            Please enter the %{factor} code
          </label>

          <!-- Label for security question factor -->
          <div><!-- Nested div is required to avoid translation update issue (See HACK in CustomerTable.vue) -->
            <label
            v-if="selectedFactor.type === 'question'" for="callerResponse"
            v-translate="{securityQuestion: $gettext(this.selectedFactor.profile.question)}">
              %{ securityQuestion }
            </label>
          </div>

          <!-- Input field for the caller's response -->
          <v-text-field
            v-model="callerResponse"
            name="callerResponse"
            :label="responseLabel"
            id="verifyResponse"
            ref="callerResponse"
            outlined
            color="#333"
            class="my-4"
            persistent-hint
            :counter="responseLengthMax"
            autocomplete="off"
            :maxlength="responseLengthMax?responseLengthMax:null"
            :clearable="true"
            :type="showResponse ? 'text' : 'password'"
            :hint="responseHint"
            :rules="responseRules"
            :class="{ 'is-invalid': !callerVerified && callerVerified !== null, 'is-valid': callerVerified && callerVerified !== null }"
            @keyup.enter="verifyCallerResponse()"
          >
            <template v-slot:append>
              <!-- control if the caller response input field is starred out or not -->
              <v-icon @click="showResponse = !showResponse">
                {{ showResponse ? 'mdi-eye' : 'mdi-eye-off' }}
              </v-icon>
            </template>
          </v-text-field>

          <div id="verify_result" v-if="callerVerified !== null">
            {{ verificationMessage }}
            <svg v-if="callerVerified !== null && callerVerified"  width="2em" height="2em" viewBox="0 0 16 16" class="bi bi-check-circle-fill float-right mr-3 success--text" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"></path>
            </svg>
            <svg v-if="callerVerified !== null && !callerVerified"  width="2em" height="2em" viewBox="0 0 16 16" class="bi bi-x-circle-fill float-right mr-3 error--text" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
            </svg>
          </div>
        </div>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Button to perform the actual caller verification against the API -->
          <v-btn
            v-if="selectedFactor && (otpFactors.includes(selectedFactor.type) || selectedFactor.type === 'question')"
            color="success"
            id="verify_verify"
            @click="verifyCallerResponse()"
            :loading="verifyInProgress"
            :disabled="responseInvalid"
          >
            <translate>Verify</translate>
          </v-btn>

          <!-- Cancel button to abort this verification dialog -->
          <v-btn
            color="primary"
            id="verify_close"
            @click="dialogVerifyResponse = false"
            :disabled="verifyInProgress"
          >
            <translate>Close</translate>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Device Flow Verification Link Selector -->
    <v-dialog
      v-model="dialogDeviceFlowDelivery"
      persistent
      max-width="625"
    >
      <v-card>
        <v-card-title v-if="selectedFactor !== null" v-translate='{factorDescription: $cvutils.getTranslatedFactorDesc(selectedFactor) }'>
          Caller Verification: %{ factorDescription }
        </v-card-title>
        <v-card-text>
          <v-radio-group v-model="deviceDeliveryMethod">
            <v-radio
              key="deliveryLink"
              id="deliveryLink"
              name="deliveryLink"
              :label="$gettext('Show the verification link and manually provide it to the caller.')"
              value="link"
              @click="clearSelectedWebhook()"
            ></v-radio>
            <v-radio
              key="deliveryLinkToWebhook"
              id="deliveryLinkToWebhook"
              name="deliveryLinkToWebhook"
              :label="$gettext('Deliver a verification link to the caller with the selected method.')"
              value="webhook"
            >
            </v-radio>
          </v-radio-group>
          <v-select
            id="cv_selected_webhook"
            v-model="selectedWebhook"
            :label="$gettext('Choose a delivery method')"
            v-if="deviceDeliveryMethod == 'webhook'"
            :items="webhooks"
          >
          </v-select>

          <v-row class="justify-end mr-2">
            <v-btn
              v-if="selectedWebhook == 'sms' && featureFlags.deviceFlowPhoneOverride"
              color="info"
              id="device_override_phone"
              @click="deviceOverrideAttribute('sms')"
              x-small
            >
              <translate key="phone_change" v-if="deviceSmsNumber || devicePhoneOverride">Change Number</translate>
              <translate key="phone_set" v-else>Set Number</translate>
            </v-btn>
            <v-btn
              v-if="selectedWebhook == 'email' && featureFlags.deviceFlowEmailOverride"
              color="info"
              id="device_override_email"
              @click="deviceOverrideAttribute('email')"
              x-small
            >
              <translate key="email_change" v-if="deviceEmailAddr || deviceEmailOverride">Change Email</translate>
              <translate key="email_set" v-else>Set Email</translate>
            </v-btn>
          </v-row>

      </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Button to perform the actual caller verification against the API -->
          <v-btn
            color="success"
            id="device_verify"
            @click="verifyCallerDevice()"
            :disabled="(deviceDeliveryMethod == null) ||
              (deviceDeliveryMethod === 'webhook' && selectedWebhook == null) ||
              (deviceDeliveryMethod === 'webhook' && selectedWebhook === 'sms' && !(devicePhoneOverride || deviceSmsNumber)) ||
              (deviceDeliveryMethod === 'webhook' && selectedWebhook === 'email' && !(deviceEmailOverride || deviceEmailAddr))"
            :loading="verifyInProgress"
          >
            <translate>Verify</translate>
          </v-btn>

          <!-- Cancel button to abort this verification dialog -->
          <v-btn
            color="primary"
            id="device_close"
            @click="dialogDeviceFlowDelivery = false"
            :disabled="verifyInProgress"
          >
            <translate>Close</translate>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <!-- CIBA Optional Message Dialog -->
    <v-dialog
      v-model="dialogCIBAMessage"
      persistent
      max-width="625"
    >
      <v-card>
        <v-card-title v-if="selectedFactor !== null" v-translate='{factorDescription: $cvutils.getTranslatedFactorDesc(selectedFactor) }'>
          Caller Verification: %{ factorDescription }
        </v-card-title>

        <div class="mb-5 pa-5">
          <!-- display predefined messages by default but hide if a custom message as selected -->
          <v-expand-transition>
            <div v-if="!useCustomMessage">
              <v-select
                id="selectedMessage"
                :item-text="item => item[$language.current] || item['en']"
                :items="featureFlags.cibaPredefinedMessages"
                item-value="id"
                v-model="selectedMessage"
                outlined
                color="#333"
                :label="$gettext('Select a message to send to the caller')"
                item-color="secondary"
              ></v-select>
            </div>
          </v-expand-transition>
          <!-- display a custom message dialog if a custom message was enabled -->
          <v-expand-transition>
            <div v-if="useCustomMessage">
              <!-- Input field for the custom message -->
              <v-text-field
                v-model="customMessage"
                name="customMessage"
                id="customMessage"
                ref="customMessage"
                outlined
                color="#333"
                class="my-4"
                persistent-hint
                autocomplete="off"
                :clearable="true"
                :label="$gettext('Custom message')"
                :hint="$gettext('Enter a custom message to send to the caller with the authentication request.')"
                @keyup.enter="verifyCallerCIBA()"
              >
              </v-text-field>
            </div>
          </v-expand-transition>

          <!-- display a checkbox to allow selection of a custom message -->
          <v-checkbox
            v-if="featureFlags.cibaCustomMessageEnabled"
            id="custom_message"
            v-model="useCustomMessage"
            dense
            :label="$gettext('Send custom message')"
          ></v-checkbox>

        </div>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Button to perform the actual caller verification against the API -->
          <v-btn
            color="success"
            id="ciba_verify"
            @click="verifyCallerCIBA()"
          >
            <translate>Verify</translate>
          </v-btn>

          <!-- Cancel button to abort this verification dialog -->
          <v-btn
            color="primary"
            id="ciba_close"
            @click="dialogCIBAMessage = false"
          >
            <translate>Close</translate>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Poll Notification Verification Dialog -->
    <v-dialog
      v-model="dialogVerifyPoll"
      persistent
      max-width="625"
    >
      <v-card>
        <v-card-title v-if="selectedFactor !== null" v-translate='{factorDescription: $cvutils.getTranslatedFactorDesc(selectedFactor) }'>
          Caller Verification: %{ factorDescription }
        </v-card-title>

          <v-card-text v-if="pollDialogText !== null">
            <label>{{ pollDialogText }}</label>
          </v-card-text>

        <div v-if="pushExpiresCountdown !== null && deviceDeliveryMethod === 'link'" class="ml-5">
          <div v-if="deviceFlowLinkMode=='url' || deviceFlowLinkMode=='both'">
            <label>{{ $gettext('Verification URL') }}</label>
            <v-chip
              class="ma-2"
              color="success"
              label
              outlined
            >
            {{ verificationUrl }}
            </v-chip>
            <v-btn
              icon
              color="success"
              @click="copyToClipBoard">
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
            <br />
          </div>
          <div v-if="deviceFlowLinkMode=='code' || deviceFlowLinkMode=='both'">
            <label>{{ $gettext('User Code') }}</label>
            <v-chip
              class="ma-2"
              color="success"
              label
              outlined
            >
            {{ verificationCode }}
            </v-chip>
            <v-btn
              icon
              color="success"
              @click="copyCodeToClipBoard">
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- push verification challenge e.g. number challenge -->
        <div v-if="pushChallenge !== null" id="push_challenge" class="ml-5">
          <translate>Push Verification Challenge Code:</translate>
          <v-chip outlined label class="ml-5" color="primary">
            {{pushChallenge}}
          </v-chip>
        </div>

        <!-- Status of the PUSH Notification (Waiting, Success, Rejected) -->
        <div id="push_result" class="ma-5">
          {{ pushMessage }}
          <svg id="push_success" v-if="pushResult !== null && pushResult === 'SUCCESS'"  width="2em" height="2em" viewBox="0 0 16 16" class="bi bi-check-circle-fill float-right mr-3 success--text" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"></path>
          </svg>
          <svg id="push_waiting" v-if="pushResult !== null && pushResult === 'WAITING'" width="2em" height="2em" viewBox="0 0 16 16" class="bi bi-hourglass-split info--text mr-3 float-right" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M2.5 15a.5.5 0 1 1 0-1h1v-1a4.5 4.5 0 0 1 2.557-4.06c.29-.139.443-.377.443-.59v-.7c0-.213-.154-.451-.443-.59A4.5 4.5 0 0 1 3.5 3V2h-1a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-1v1a4.5 4.5 0 0 1-2.557 4.06c-.29.139-.443.377-.443.59v.7c0 .213.154.451.443.59A4.5 4.5 0 0 1 12.5 13v1h1a.5.5 0 0 1 0 1h-11zm2-13v1c0 .537.12 1.045.337 1.5h6.326c.216-.455.337-.963.337-1.5V2h-7zm3 6.35c0 .701-.478 1.236-1.011 1.492A3.5 3.5 0 0 0 4.5 13s.866-1.299 3-1.48V8.35zm1 0c0 .701.478 1.236 1.011 1.492A3.5 3.5 0 0 1 11.5 13s-.866-1.299-3-1.48V8.35z"/>
          </svg>
          <svg id="push_reject" v-if="pushResult !== null && pushResult !== 'SUCCESS' && pushResult !== 'WAITING'"  width="2em" height="2em" viewBox="0 0 16 16" class="bi bi-x-circle-fill float-right mr-3 error--text" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
          </svg>
        </div>

        <!-- PUSH Verification Countdown UI -->
        <div v-if="pushExpiresCountdown !== null" class="text-center my-2">
          <v-divider></v-divider>
          <div id="push_countdown" class="pa-4">
            <v-progress-linear height="40" color="primary" :value="pushExpiresProgress">
              <strong>{{ pushExpiresCountdown }}</strong>
            </v-progress-linear>
          </div>
        </div>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Close button to exit this verification dialog -->
          <v-btn
            color="primary"
            id="verify_push_close"
            @click="dialogVerifyPoll = false"
          >
            <translate>Close</translate>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- email delivery override dialog -->
    <v-dialog
      v-model="dialogEmailOverride"
      persistent
      max-width="625"
    >
      <v-card>
        <v-card-title id="email_override_title" v-translate>
          Set email address to send verification link to
        </v-card-title>

        <div class="ma-5">
          <!-- Email Override Dialog -->
          <v-text-field
            v-model="deliveryOverrideValue"
            name="emailOverride"
            :label="$gettext('Enter email address')"
            id="emailOverride"
            ref="emailOverride"
            outlined
            color="#333"
            class="my-4"
            autocomplete="off"
            :clearable="true"
            :rules="emailRules"
            @keyup.enter="deliveryOverrideSet()"
          >
          </v-text-field>
        </div>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Button to perform the actual caller verification against the API -->
          <v-btn
            color="success"
            id="email_override_update"
            :disabled="!emailValid"
            @click="deliveryOverrideSet()"
          >
            <translate>Update</translate>
          </v-btn>

          <!-- Cancel button to abort this verification dialog -->
          <v-btn
            color="primary"
            id="email_override_cancel"
            @click="dialogEmailOverride = false"
          >
            <translate>Cancel</translate>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- phone delivery override dialog -->
    <v-dialog
      v-model="dialogPhoneOverride"
      persistent
      max-width="625"
    >
      <v-card>
        <v-card-title id="phone_override_title" v-translate>
          Set phone number to send verification link to
        </v-card-title>

        <div class="ma-5">
          <!-- Phone Override Dialog -->
          <vue-tel-input-vuetify
            v-model="deliveryOverrideValue"
            :inputId="'delivery_phone'"
            :inputvalue="deliveryOverrideValue"
            outlined
            dense
            :mode="'international'"
            :defaultCountry="'US'"
            :onlyCountries="['CA', 'US']"
            :selectLabel="$gettext('Country')"
            autocomplete="off"
            required
            :placeholder="$gettext('Enter a phone number')"
            :label="$gettext('Enter a phone number')"
            appendIcon="$dropdown"
            @input="onPhoneInput"
            >
          </vue-tel-input-vuetify>
        </div>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Button to perform the actual caller verification against the API -->
          <v-btn
            color="success"
            id="phone_override_update"
            :disabled="!(deliveryPhone.valid || !deliveryOverrideValue)"
            @click="deliveryOverrideSet()"
          >
            <translate>Update</translate>
          </v-btn>

          <!-- Cancel button to abort this verification dialog -->
          <v-btn
            color="primary"
            id="phone_override_cancel"
            @click="dialogPhoneOverride = false"
          >
            <translate>Cancel</translate>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-row>

</template>

<script>
import {translate} from 'vue-gettext';

const {gettext: $gettext, gettextInterpolate} = translate;

export default {
  name: 'VerifyUser',

  // Component properties
  //
  // profile - project object for the selected user.
  // value   - controls if the dialog is visible or hidden.
  //
  props: {
    profile: Object,
    value: Boolean,
    quickVerifyFactorId: String,
    featureFlags: Object,
    deliveryMethods: Object,
    verifyMode: String,
    delegatorUID: String,
  },
  watch: {
    quickVerifyFactorId: function(factorId) {
      if(factorId !== null) {
        this.verifyCallerInitiate(factorId);
      }
      // reset the trigger property so the next QuickVerify triggers this watch again.
      this.$emit('qv_reset');
    },
    deliveryOverrideValue: function (mail) {
      if (this.deliveryOverrideMethod === 'email') {
        if (mail && mail !== '') {
          this.emailRules = [ value => {
            if (/.+@.+\..+/.test(value)) return true
            return 'E-mail must be valid.'
          }];
          this.emailValid = this.$refs.emailOverride.validate();
        }
        else if (!mail || mail === '') {
          this.emailRules = []
          this.emailValid = true;
        }
      }
    }
  },
  computed: {
    // expose a computed parameter that returns the :value property of this
    // component on retrieval and emits an close event to the parent to close
    // the dialog when set to 'false'.
    dialogSelectMethod: {
      get() {
        return this.value;
      },
      set(value) {
        if(!value) {
          this.$emit('close', 'mfa-verify');
        }
      }
    },
    phoneLinkLabel() {
      let phoneLabel = $gettext('SMS link to caller')
      let phoneNumber = this.devicePhoneOverride || this.deviceSmsNumber;
      if (phoneNumber) {
        phoneLabel = phoneLabel + '  (' + phoneNumber + ')';
      }
      return phoneLabel;
    },
    emailLinkLabel: {
      get() {
        let emailLabel = $gettext('Email link to caller')
        let emailAddress = this.deviceEmailOverride || this.deviceEmailAddr;
        if(emailAddress) {
          emailLabel = emailLabel + '  (' + emailAddress + ')';
        }
        return emailLabel;
      }
    },
    responseRules() {
      if(this.selectedFactor.type === 'question') {
        return [];
      }
      else if(this.selectedFactor.provider === 'RSA') {
        return this.rsaRules;
      }
      else {
        return this.otpRules;
      }
    },
    // FIXME: we should make this a return property from our API call so we don't have to do this.
    callerName() {
      if(this.profile && this.profile.profile) {
        return this.profile.profile.firstName + ' ' + this.profile.profile.lastName;
      }
      else {
        return "";
      }
    },
    callerEmail(){
      if(this.profile && this.profile.profile){
        return this.profile.profile.email;
      }
      return "";
    },
    isRegularVerify(){
      if( this.verifyMode === 'caller'){
        return true;
      }
      return false;
    },
    hasActiveFactor(){
      for(const [,value] of Object.entries(this.profile.mfa.factors)) {
        let factor = value;
        if( factor.status == 'ACTIVE'){
          return true;
        }
      }
      return false;
    },
    getFailReason(){
      if(!this.profile){
        return "Didn't find the manager's profile.  "+this.notification;
      }
      else if(!this.profile.mfa || !this.hasActiveFactor){
        return "Manager does not have supported verification methods.";
      }
      return "Can't verify for unknown reason";
    },
    canVerify(){
      if(this.profile && this.profile.mfa){
        // as long as we can find one active factor, this can be verified
        // if not returning, then didn't find active factor, can't verify
        if(this.hasActiveFactor){
          return true;
        }
        return false;
      }
      else{
        return false;
      }
    },
    // filter factors to only active, pending and disabled for the selected customer
    activePendingDisabledFactors() {
      let activePendingDisabled = {};
      // make sure we have a valid profile and MFA object first and foremost.
      if(this.profile && this.profile.mfa) {
        // loop over all factors, and filter out active and pending factors.
        for(let key in this.profile.mfa.factors) {
          let factor = this.profile.mfa.factors[key];
          if(['ACTIVE', 'PENDING_ACTIVATION', 'DISABLED'].includes(factor.status)) {
            activePendingDisabled[factor.id] = factor;
          }
        }
      }
      return activePendingDisabled;
    },
    // determine the verification link display mode
    deviceFlowLinkMode(){
      return this.featureFlags.deviceFlowLinkMode;
    },
    // computed property to determine if the Verify button should be
    // disabled or enabled.
    responseInvalid() {
      // make sure we have some sort of response at a minimum
      if(this.callerResponse && this.callerResponse.length > 0) {
        // if this.responseLengthMin is not set, we are dealing with a security question
        // so long as we have one character, let it through.
        if(!this.responseLengthMin) {
          return false; // button is not disabled (enabled)
        }

        // select a character set based on the token type.
        let charset = '[0-9]'; // most OTP's are numeric only.
        if(this.selectedFactor.provider === 'RSA') {
          // RSA tokens support alphanumeric.
          charset = '[0-9a-zA-Z]';
        }
        // regex to make sure we have between min and max characters.
        let pattern = `^(${charset}{${this.responseLengthMin},${this.responseLengthMax}})$`
        return !(RegExp(pattern).test(this.callerResponse))
      }
      return true;
    }
  },
  data: () => ({
    selectedFactor: {},
    webhooks: [],
    selectedWebhook: null,

    dialogVerifyPoll: false,
    dialogVerifyResponse: false,
    dialogCIBAMessage: false,
    dialogDeviceFlowDelivery: false,
    dialogEmailOverride: false,
    dialogPhoneOverride: false,

    // attributes for the caller response input dialog/field.
    showResponse: false,
    callerResponse: null,
    callerVerified: null,
    verificationMessage: null,
    responseHint: null,
    responseLengthMin: null,
    responseLengthMax: null,
    responseLabel: null,
    verifyInProgress: false,

    // push notification variables/parameters
    pollDialogText: null,
    pushMessage: null,
    pushResult: 'WAITING',
    pushExpires: null,
    pushExpiresCountdown: null,
    pushExpiresProgress: 0,
    pushChallenge: null,
    transactionId: null,
    transactionType: null,
    // used for sending messages along with the custom_app push notification.
    selectedMessage: null,
    customMessage: null,
    useCustomMessage: false,

    // used for device flow
    deviceDeliveryMethod: null,
    verificationUrl: null,
    verificationCode: null,
    deliveryOverrideValue: null,
    deviceEmailOverride: null,
    devicePhoneOverride: null,
    deliveryAttrValue: null,
    emailRules: [],
    deliveryPhone: {
      number: '',
      valid: false,
      country: undefined
    },
    emailValid: false,

    rsaRules: [
      v => (!v || /^([0-9a-zA-Z])*$/.test(v)) || $gettext('Code must contain letters and numbers only.')
    ],
    otpRules: [
      v => (!v || /^([0-9])*$/.test(v)) || $gettext('Code must contain numbers only.')
    ],

    otpFactors: ['token:software:totp','token:hotp','sms','email','token']
  }),
  methods: {
    onPhoneInput(formattedNumber, {number, valid, country}) {
      this.deliveryPhone.number = number.international;
      this.deliveryPhone.valid = valid;
      this.deliveryPhone.country = country.name;
    },
    async initializeWebHooks() {

      this.webhooks = [];
      // only show sms as an option if the feature is enabled, and we either have a number or we have phone override enabled.
      var enableSMSDelivery = this.featureFlags.deviceFlowDeliverySMS && (this.featureFlags.deviceFlowPhoneOverride || this.deviceSmsNumber);
      if(enableSMSDelivery) {
        this.webhooks.push({'value': 'sms', 'text': this.phoneLinkLabel });
      }

      // only show email as an option if the feature is enabled, and we either have a number or we have email override enabled.
      var enableEmailDelivery = this.featureFlags.deviceFlowDeliveryEmail && (this.featureFlags.deviceFlowEmailOverride || this.deviceEmailAddr);
      if(enableEmailDelivery) {
        this.webhooks.push({'value': 'email', 'text': this.emailLinkLabel });
      }

      for (const [key, value] of Object.entries(this.deliveryMethods)) {
        this.webhooks.push({'value':key,'text':value});
      }
    },
    // Start the caller verification process, trigging any challenges and
    // displaying the verification dialog.
    verifyCallerInitiate: async function(factorId) {
      // Reset the TOTP validation status and message
      this.callerResponse = null;
      this.callerVerified = null;
      this.verificationMessage = null;
      this.pushChallenge = null;
      this.transactionId = null;
      // keep track of the selected factor for verification purposes.
      this.selectedFactor = this.profile.mfa.factors[factorId];

      if (this.selectedFactor) {

        // close the verification method selection dialog
        this.dialogSelectMethod = false;

        // Verify an OTP factor (Software TOTP, SMS, Email)
        if (this.otpFactors.includes(this.selectedFactor.type)) {

          const translatedLabel = $gettext('%{factor} Code')
          this.responseLabel = gettextInterpolate(translatedLabel, {factor: this.$cvutils.getTranslatedFactorDesc(this.selectedFactor) })
          this.responseLengthMin = 6; /* min length */
          this.responseLengthMax = 6; /* max length */

          // determine the message to display based upon the type of OTP verification we are going to perform.
          if(this.selectedFactor.type === 'token:software:totp' || this.selectedFactor.type === 'token:hotp') {
            this.responseHint = $gettext('This is usually a 6 digit code within the authentication mobile application.');

            // totp code, no challenge needs to be sent to the caller, just display the entry dialog.
            this.dialogVerifyResponse = true;

            // Set focus to the input field for the caller response.
            setTimeout(() => { this.$refs.callerResponse.focus(); }, 200);
          }
          else if(this.selectedFactor.type === 'token' && this.selectedFactor.provider === 'RSA') {
            this.responseHint = $gettext('The RSA code is a combination of token PIN plus the current token code.');

            // no challenge needs to be sent to the caller, just display the entry dialog.
            this.dialogVerifyResponse = true;
            this.responseLengthMin = 4; /* min length */
            this.responseLengthMax = 16; /* max length */

            // Set focus to the input field for the caller response.
            setTimeout(() => { this.$refs.callerResponse.focus(); }, 200);
          }
          // SMS & Email need us to send a code to the caller via an API call first.
          else if(['sms','email'].includes(this.selectedFactor.type)) {
            if(this.selectedFactor.type === 'sms') {
              const translated = $gettext('A 6 digit code has been sent to the caller via SMS to: %{phone_number}');
              this.responseHint = gettextInterpolate(translated, {phone_number: this.selectedFactor.profile.phone_number});
            }
            else if(this.selectedFactor.type === 'email') {
              const translated = $gettext('A 6 digit code has been emailed to the caller at: %{email}');
              this.responseHint = gettextInterpolate(translated, {email: this.selectedFactor.profile.email});
            }

            // for email and sms based opt, we need to trigger the sending of the code first, do that here.
            const {status, notification, challengeResponse} = await this.$cvApi.factorChallenge(this.profile.id, this.selectedFactor.id, this.selectedFactor.type, this.delegatorUID);

            if(status) {
                // CLAIM: if we got here, we got the expected response back, so we can only assume
                // the code was sent to the user via SMS or Email correctly.  Now we need to present
                // the help desk agent with a dialog to enter the caller response, close the selection
                // dialog and open up the verification dialog.
                this.dialogVerifyResponse = true;

                // get the transaction id (if present) for OOB flows.
                this.transactionId = challengeResponse.transactionId

                // Set focus to the input field for the caller response.
                setTimeout(() => { this.$refs.callerResponse.focus(); }, 200);
            }
            else {
              // display error notification to the user
              this.$emit('notify', notification);
            }
          } // end - SMS / Email OTP.
        }
        else if (this.selectedFactor.type === 'push') {
          // Trigger the PUSH and poll for the result
          const {status, notification, challengeResponse} = await this.$cvApi.factorChallenge(this.profile.id, this.selectedFactor.id, this.selectedFactor.type, this.delegatorUID)

          if(status && challengeResponse != null) {
            // CLAIM if we got here the push notification was sent, so no we can extract the
            // transaction ID in order to poll for a response from the caller.
            this.transactionId = challengeResponse.transactionId;
            this.transactionType = 'push';
            this.pushChallenge = challengeResponse.challengeCode; // number challenge (if present)
            if (this.pushChallenge) {
              this.pollDialogText = $gettext('Ask the caller to select or enter the push challenge code shown below.');
            }
            else {
              this.pollDialogText = null;
              this.pushChallenge = null;
            }

            // Check for polling status at the configured interval
            this.pushMessage = $gettext(challengeResponse.message); // display initial push notification waiting message.
            this.pushResult = challengeResponse.result;
            setTimeout(this.pollForVerifyResult, 5000);

            // Trigger the countdown timer, if 'expiresAt' exists
            const expiresAt = challengeResponse.expiresAt;
            if (expiresAt) {
              this.pushExpires = expiresAt;
              setTimeout(this.expiresCountdown, 1000);
            }

            // Push notification sent, display the push verification dialog.
            this.dialogVerifyPoll = true;
          }
          else {
            // display error notification to the user
            this.$emit('notify', notification);
          }
        }
        else if (this.selectedFactor.type === 'device') {
          // reset parameters.
          this.deviceDeliveryMethod = null;
          this.selectedWebhook = null;
          this.verificationUrl = null;
          this.dialogEmailOverride = false;
          this.dialogPhoneOverride = false;
          this.deliveryOverrideValue = null;

          // if we don't have email or sms configured default to link
          if(!this.featureFlags.deviceFlowDeliverySMS && !this.featureFlags.deviceFlowDeliveryEmail) {
            this.deviceDeliveryMethod = 'link';
          }

          // load defaults for the email a phone number for device delivery.
          this.deviceSmsNumber = this.$cvutils.getFactorProfileAttribute(this.selectedFactor, 'phone_number');
          this.deviceEmailAddr = this.$cvutils.getFactorProfileAttribute(this.selectedFactor, 'email');

          // setup the webhooks
          await this.initializeWebHooks();

          // display the delivery method selection dialog
          // or if there is no webhooks configured, directly take the control to manual display page
          if(this.webhooks.length>0){
            this.dialogDeviceFlowDelivery = true;
          }
          else{
            this.deviceDeliveryMethod = 'link';
            this.verifyCallerDevice();
          }
        }
        // handle CIBA flow.
        else if(this.selectedFactor.type === 'custom_app') {

          // reset any message parameters.
          this.useCustomMessage = false;
          this.customMessage = null;
          this.selectedMessage = null;

          // if we have more then one predefined message or we have custom messages enabled, then display a message
          // section dialog for the agent to pick from or provide custom one.
          if(this.featureFlags.cibaCustomMessageEnabled || this.featureFlags.cibaPredefinedMessages.length > 1) {
            this.dialogCIBAMessage = true;
          }
          // otherwise just move right to the push.
          else {
            this.verifyCallerCIBA();
          }
        }
        // Security Question Verification method.
        else if (this.selectedFactor.type === 'question') {
          this.responseLabel = $gettext('Answer');
          this.responseHint = $gettext('Enter the callers answer above');
          this.responseLengthMin = null;
          this.responseLengthMax = null;

          // Security Question: Just display the entry dialog.
          this.dialogVerifyResponse = true;
        }
      }
      // Unable to retrieve the selected factor details - this should be impossible
      // display and error, and let them try a different factor that perhaps works.
      else {
        this.$emit('notify', {
          title: 'Verification Error',
          body: 'Unable to retrieve the selected factor details.',
          type: 'error',
          timeout: -1
        });
      }
    },
    getWebhookDesc(webhookName){
      var desc = this.deliveryMethods[webhookName] || webhookName;
      return desc;
    },
    // Function to poll for the response from verification attempt (push, ciba, or device)
    pollForVerifyResult: async function() {
      // Stop polling if the dialog has been closed
      if (!this.dialogVerifyPoll) {
        return;
      }
      if (this.transactionId !== null) {

        // we also need the login id for CIBA flow.
        let login = null;
        if (this.transactionType === 'ciba') {
          login = this.profile.profile.login;
        }
        // poll for the push, device or ciba flow result based on the transaction id.
        const {status, notification, result, message} =
          await this.$cvApi.factorPoll(this.profile.id, this.selectedFactor.id, this.transactionId, login, this.delegatorUID);
        if(status) {
          // still waiting for push notification response
          if (result && result === 'WAITING') {
            this.pushMessage = $gettext(message); // display waiting message.
            this.pushResult = result;

            // try again in 5 seconds.
            setTimeout(this.pollForVerifyResult, 5000);
          }
          // we got back a non-waiting response, display the result.
          else if (result) {
            this.transactionId = this.transactionType = null;
            this.pushMessage = $gettext(message); // display result messages -- likely success/failure
            this.pushResult = result;
            this.pollDialogText = null;
            if(this.pushResult == "SUCCESS") {
              this.$emit('verifySuccess', this.profile, this.selectedFactor);
            }
            this.selectedWebhook = null;
          }
        }
        else {
          // Close the polling for push notification dialog.
          this.dialogVerifyPoll = false;

          // display error notification to the user
          this.$emit('notify', notification);
        }
      }
      else {
        // no transaction id, we can't pool, kill this dialog.
        this.dialogVerifyPoll = false;

        // display an error notification saying we can't poll.
        this.$emit('notify', {
          title: 'MFA Verification Error',
          body: 'Unable to poll for push verification result.',
          type: 'error',
          timeout: -1
        });
      }
    },
    // function to handle the push countdown timer
    expiresCountdown: function() {
      let expiresAt = this.pushExpires;

      if (this.pushResult === 'SUCCESS' || this.pushResult === 'REJECTED') {
        this.pushExpiresCountdown = null;
        this.pushChallenge = null; // clear challenge display as well
        return;
      }

      if (expiresAt === undefined || expiresAt === null || expiresAt === '') {
        this.pushExpiresCountdown = null;

        if (this.dialogVerifyPoll) {
          this.dialogVerifyPoll = false;

          this.$emit('notify', {
            title: 'Caller Verification Timeout',
            body: 'The caller verification has timed out.',
            type: 'warning',
            timeout: 10000
          });
        }
        return;
      }

      const now = new Date();
      const expiresDate = new Date(expiresAt);

      // Calculate the difference in milliseconds
      // We add 2000 ms here to make sure the time waiting is enough for us to get the TIMEOUT result as loading the UI adds a bit of delay
      const difference = expiresDate - now + 2000;

      // Check if we have already expired
      if (difference <= 0) {
        this.pushExpiresCountdown = null;

        // Close the dialog if still visible
        if (this.dialogVerifyPoll) {
          this.dialogVerifyPoll = false;

          this.$emit('notify', {
            title: $gettext('Caller Verification Timeout'),
            body: $gettext('The caller verification has timed out.'),
            type: 'warning',
            timeout: 10000
          });
        }
        return;
      }

      // Calculate the difference in seconds
      const diffSeconds = difference / 1000;

      // Push expiry is 5 minutes (300 seconds)
      var totalSeconds = 300;
      if(this.transactionType=='push'){
        totalSeconds = ( this.featureFlags.verifyPushCountdown >= 30 && this.featureFlags.verifyPushCountdown <= 300 )?this.featureFlags.verifyPushCountdown:300;
      }
      else if(this.transactionType=='device'){
        totalSeconds = ( this.featureFlags.verifyDeviceCountdown >= 30 && this.featureFlags.verifyDeviceCountdown <= 300 )?this.featureFlags.verifyDeviceCountdown:300;
      }
      else if(this.transactionType=='ciba'){
        totalSeconds = ( this.featureFlags.verifyCibaCountdown >= 30 && this.featureFlags.verifyCibaCountdown <= 300 )?this.featureFlags.verifyCibaCountdown:300;
      }
      // use the set total timeout instead of hard coded 300
      const progress = Math.ceil((diffSeconds / totalSeconds) * 100);
      this.pushExpiresProgress = progress;

      // Calculate the difference in minutes
      const minutes = diffSeconds / 60;
      let minutesRemaining = Math.trunc(minutes);
      if (minutesRemaining < 10) {
        minutesRemaining = `0${minutesRemaining}`;
      }

      // Calculate the remaining seconds
      const seconds = (minutes - minutesRemaining) * 60;
      let secondsRemaining = Math.trunc(seconds);
      if (secondsRemaining < 10) {
        secondsRemaining = `0${secondsRemaining}`;
      }

      this.pushExpiresCountdown = `${minutesRemaining}:${secondsRemaining} ` + $gettext('min(s)');

      // update the countdown in 500 ms.
      setTimeout(this.expiresCountdown, 500);
    },
    updateWebhookDesc: function(hookId) {
      for(var i = 0; i < this.webhooks.length; i++) {
        if(this.webhooks[i]['value'] === hookId) {
          if(hookId == 'sms') {
            this.webhooks[i]['text'] = this.phoneLinkLabel;
          }
          else if(hookId == 'email') {
            this.webhooks[i]['text'] = this.emailLinkLabel;
          }
        }
      }
    },
    clearSelectedWebhook(){
      this.selectedWebhook = null;
    },

    deviceOverrideAttribute: function(method) {
      this.deliveryOverrideValue = null;
      this.deliveryOverrideMethod = method;

      // if email is selected, present the email override dialog.
      if(method === 'email') {
        this.dialogEmailOverride = true;
        // if we are overriding email, and we have already overridden it,
        // make sure we start with our overridden value.
        if(this.deviceEmailOverride) {
          this.deliveryOverrideValue = this.deviceEmailOverride;
        }
      }
      // else if sms is selected present the phone override dialog.
      else if(method == 'sms') {
        this.dialogPhoneOverride = true;
        // if we are overriding phone, and we have already overridden it,
        // make sure we start with our overridden value.
        if(this.devicePhoneOverride) {
          this.deliveryOverrideValue = this.devicePhoneOverride;
        }
      }

    },
    deliveryOverrideSet: function() {
      if(this.deliveryOverrideValue && this.deliveryOverrideValue !== '') {
        if(this.deliveryOverrideMethod == 'sms') {
          this.devicePhoneOverride = this.deliveryOverrideValue;
        }
        else if (this.deliveryOverrideMethod == 'email') {
          this.deviceEmailOverride = this.deliveryOverrideValue;
        }
      }
      // cleared values fallback to system defaults.
      else {
        if(this.deliveryOverrideMethod == 'sms') {
          this.devicePhoneOverride = null;
        }
        else if (this.deliveryOverrideMethod == 'email') {
          this.deviceEmailOverride = null;
        }
      }
      this.updateWebhookDesc(this.deliveryOverrideMethod);
      this.dialogPhoneOverride = false;
      this.dialogEmailOverride = false;
      this.deliveryOverrideMethod = null;
      this.deliveryOverrideValue = null;
    },
    verifyCallerDevice: async function() {

      // put a loading spinner on the verify button.
      this.verifyInProgress = true;

      // reset override so we only use it if needed.
      this.deliveryAttrValue = null;

      // for email and sms based opt, we need to trigger the sending of the code first, do that here.
      let verifyEndpoint = null;
      if(this.selectedWebhook) {
        if(this.selectedWebhook === 'sms'){
          this.deviceDeliveryMethod = 'sms';

          // if the phone number was overridden use that.
          if(this.devicePhoneOverride) {
            this.deliveryAttrValue = this.devicePhoneOverride;
          }
        }
        else if(this.selectedWebhook === 'email'){
          this.deviceDeliveryMethod = 'email';

          // if the email was overridden use that.
          if(this.deviceEmailOverride) {
            this.deliveryAttrValue = this.deviceEmailOverride;
          }
        }
        else{
          verifyEndpoint = this.selectedWebhook;
        }
      }
      else if (this.deviceDeliveryMethod != 'link'){
        // display error notification to the user
        this.verifyInProgress = false;
        this.$emit('notify', {
          title: 'Caller Verify Failed',
          body: 'Please select a delivery method.',
          type: 'warning'
        });
        return;
      }
      // trigger the device flow challenge.
      const {status, notification, challengeResponse} =
        await this.$cvApi.factorChallengeDevice(
          this.profile.id,
          this.selectedFactor.id,
          this.deviceDeliveryMethod,
          this.deliveryAttrValue,
          this.delegatorUID,
          verifyEndpoint
        );

      // dismiss the selector dialog and spinner.
      this.dialogDeviceFlowDelivery = false;
      this.verifyInProgress = false;

      if(status) {
        // CLAIM if we got here the device flow has been started, so no we can extract the
        // transaction ID in order to poll for a response from the caller.
        this.transactionId = challengeResponse.transactionId;
        this.transactionType = 'device';
        this.verificationUrl = challengeResponse.verificationUrl;
        this.verificationCode = challengeResponse.userCode;

        // parse the URL so we can extract the code and link
        let deviceUrl = new URL(this.verificationUrl);
        var deviceBaseUrl = deviceUrl.origin + deviceUrl.pathname;

        // Check for polling status at the configured interval
        if (this.deviceDeliveryMethod === 'sms') {
          this.pollDialogText = $gettext("A verification link has been sent to the caller's phone: ") +
            (this.devicePhoneOverride || this.deviceSmsNumber)
        }
        else if (this.deviceDeliveryMethod === 'email') {
          this.pollDialogText = $gettext("A verification link has been sent to the caller's email: ") +
            (this.deviceEmailOverride || this.deviceEmailAddr)
        }
        else if (this.deviceDeliveryMethod === 'webhook') {
          this.pollDialogText = $gettext("A verification link has been sent to the caller using: ") +
            this.getWebhookDesc(this.selectedWebhook);
        }
        // display link
        else {
          if(this.deviceFlowLinkMode == 'url' ){
            this.pollDialogText = $gettext("Provide the following link to the caller to verify their identity:");
          }
          else if(this.deviceFlowLinkMode == 'code'){
            let translated = "Have the caller go to %{ urlLink } and enter the user code below:"
            this.pollDialogText = gettextInterpolate(translated, {urlLink: deviceBaseUrl})
          }
          else if(this.deviceFlowLinkMode == 'both'){
            this.pollDialogText = $gettext("Provide either the following link or code to the caller to verify their identity:");
          }
        }

        this.pushMessage = $gettext(challengeResponse.message); // display initial push notification waiting message.
        this.pushResult = challengeResponse.result;
        setTimeout(this.pollForVerifyResult, 5000);

        // Trigger the countdown timer, if 'expiresAt' exists
        const expiresAt = challengeResponse.expiresAt;
        if (expiresAt) {
          this.pushExpires = expiresAt;
          setTimeout(this.expiresCountdown, 1000);
        }
        this.selectedWebhook = null;
        // device flow started, trigger the polling countdown timer.
        this.dialogVerifyPoll = true;
      }
      else {
        // display error notification to the user
        this.$emit('notify', notification);
        this.selectedWebhook = null;
      }

    },
    verifyCallerCIBA: async function() {
      this.dialogCIBAMessage = false; /* in case it was opened */

      let locale = this.profile.profile.locale;
      let login = this.profile.profile.login;
      let customMessage = this.useCustomMessage?this.customMessage:null;


      // for email and sms based opt, we need to trigger the sending of the code first, do that here.
      const {status, notification, challengeResponse} =
        await this.$cvApi.factorChallengeCIBA(this.profile.id, this.selectedFactor.id, login,
                                              locale, this.selectedMessage, customMessage, this.delegatorUID);

      if(status) {
        // CLAIM if we got here the push notification was sent, so no we can extract the
        // transaction ID in order to poll for a response from the caller.
        this.transactionId = challengeResponse.transactionId;
        this.transactionType = 'ciba';

        // Check for polling status at the configured interval
        this.pollDialogText = null
        this.pushMessage = $gettext(challengeResponse.message); // display initial push notification waiting message.
        this.pushResult = challengeResponse.result;
        setTimeout(this.pollForVerifyResult, 5000);

        // Trigger the countdown timer, if 'expiresAt' exists
        const expiresAt = challengeResponse.expiresAt;
        if (expiresAt) {
          this.pushExpires = expiresAt;
          setTimeout(this.expiresCountdown, 1000);
        }

        // Push notification sent, display the push verification dialog.
        this.dialogVerifyPoll = true;
      }
      else {
        // display error notification to the user
        this.$emit('notify', notification);
      }
    },
    verifyCallerResponse: async function() {
      // Ignore empty
      if (this.callerResponse === null || (this.callerResponse && this.callerResponse.trim() === '')) {
        return;
      }

      // show "in progress" indicator for the verification.
      this.verifyInProgress = true;

      // make sure the selected factor is a supported one for this verify function
      if(this.selectedFactor && this.selectedFactor.type &&
         (this.otpFactors.includes(this.selectedFactor.type) || this.selectedFactor.type === 'question')) {

        const {status, notification, message} =
          await this.$cvApi.factorVerify(this.profile.id, this.selectedFactor.id,
                                         this.selectedFactor.type, this.callerResponse,
                                         this.transactionId, this.delegatorUID)

        // Clear "in progress" indicator now that we have an response.
        this.verifyInProgress = false;

        if(status) {
          // We received a success HTTP status from the API call, and the response is
          // well formatted --> Caller is Verified!!
          this.callerVerified = true;

          // emit the details of the success up the chain.
          this.$emit('verifySuccess', this.profile, this.selectedFactor)

          // For now we dump the response from the successful verification API call
          // directly to the user, eventually we should probably handle these messages
          // completely in the front end so we can change them without changing the API.
          this.verificationMessage = $gettext(message);
        }
        else {
          if(notification) {
            // close the verification dialog and display error.
            this.dialogVerifyResponse = false;
            this.$emit('notify', notification);
          }
          // else, failure, but no notification -- this means a verify failure (incorrect answer/code).
          else {
            this.callerVerified = false; // flag the input field to show a red X

            // display a message depending on the type of response (code vs security question answer)
            if(this.selectedFactor.type === 'question') {
              this.verificationMessage = $gettext('Failed to verify caller:  The supplied answer was invalid.');
            }
            else {
              this.verificationMessage = $gettext('Failed to verify caller:  The supplied code was invalid.');
            }
          }
        }
      }
      // should not be able to get into this else, but if we do, it means the factor is
      // not supported so display an error saying so.
      else {
        this.dialogVerifyResponse = false;

        this.$emit('notify', {
          title: 'Caller Verify Failed',
          body: 'The selected factor is not supported, unable to verify the caller.',
          type: 'warning'
        });
      }
    },
    copyToClipBoard() {
      navigator.clipboard.writeText(this.verificationUrl);
    },
    copyCodeToClipBoard() {
      navigator.clipboard.writeText(this.verificationCode);
    }
  }
}
</script>
